import { useCallback, useEffect, useState } from 'react';

import * as monthUtils from '@actual-app/core/shared/months';

import { useAccounts } from '#hooks/useAccounts';
import { useFailedAccounts } from '#hooks/useFailedAccounts';
import { uncategorizedTransactions } from '#queries';
import { aqlQuery } from '#queries/aqlQuery';

import { montarContasFixas } from './contasFixas';
import type { ContaDoDono, Fila, LancamentoCru } from './filaPendencias';
import { FILA_VAZIA, montarFila } from './filaPendencias';
import { useAgendadosDoMes } from './useAgendadosDoMes';
import { useMesFinanceiro } from './useMesFinanceiro';

type Retorno = {
  fila: Fila;
  carregando: boolean;
  erro: Error | null;
  /**
   * Refaz a consulta dos lançamentos sem categoria. A tela chama isto depois
   * de classificar — sem ele o item continuaria na lista até um F5, e o dono
   * classificaria o mesmo lançamento duas vezes achando que o primeiro
   * toque não pegou.
   */
  recarregar: () => void;
};

/**
 * A consulta dos lançamentos SEM CATEGORIA.
 *
 * ⭐ Parte de `uncategorizedTransactions()` — a query NATIVA do Actual (a
 * mesma da tela mobile deles), que já exclui conta fora do orçamento e
 * transferência entre contas on-budget. Não reescrevemos essa régua; só
 * acrescentamos o `select` com os campos que a tela e o motor precisam.
 *
 * ⚠️ `splits: 'inline'` é ESCRITO, não herdado do default: com ele o
 * executor AQL acrescenta `is_parent = 0` ao WHERE (lançamento-pai de split
 * fora) e mantém os FILHOS — que são justamente quem precisa de categoria.
 * `'none'` faria o contrário (pais dentro, filhos fora) e `'grouped'`/`'all'`
 * trariam os pais de volta. A trava `split-pai` do motor é a rede redundante
 * pra essa escolha — ver `filaPendencias.ts`, risco R4 do spec.
 */
function consultaSemCategoria() {
  return uncategorizedTransactions()
    .options({ splits: 'inline' })
    .select([
      'id',
      'date',
      'amount',
      'is_parent',
      'category',
      'imported_payee',
      { nomeDeQuemRecebe: 'payee.name' },
      { contaDeTransferencia: 'payee.transfer_acct' },
      { transferenciaForaDoOrcamento: 'payee.transfer_acct.offbudget' },
      { nomeDaConta: 'account.name' },
      { contaForaDoOrcamento: 'account.offbudget' },
    ]);
}

function normalizar(brutos: Array<Record<string, unknown>>): LancamentoCru[] {
  return brutos.map(t => ({
    id: String(t.id),
    date: String(t.date),
    amount: Number(t.amount ?? 0),
    nomeDeQuemRecebe: (t.nomeDeQuemRecebe as string | null) ?? null,
    nomeImportado: (t.imported_payee as string | null) ?? null,
    conta: (t.nomeDaConta as string | null) ?? null,
    ehPai: Boolean(t.is_parent),
    contaForaDoOrcamento: Boolean(t.contaForaDoOrcamento),
    contaDeTransferencia: (t.contaDeTransferencia as string | null) ?? null,
    transferenciaForaDoOrcamento:
      t.transferenciaForaDoOrcamento === null ||
      t.transferenciaForaDoOrcamento === undefined
        ? null
        : Boolean(t.transferenciaForaDoOrcamento),
    temCategoria: t.category !== null && t.category !== undefined,
  }));
}

/**
 * Liga o motor puro (`montarFila`) nas fontes de dado — é o par de
 * `useMesFinanceiro`/`useAgendadosDoMes` para a tela **Pendências**.
 *
 * Três fontes, nenhuma tabela nova:
 * 1. **lançamentos sem categoria** — `uncategorizedTransactions()` (nativa);
 * 2. **contas com falha de sincronização** — `useAccounts()` (o
 *    `bank_sync_status` persistido) + `useFailedAccounts()` (as falhas desta
 *    sessão, no redux). Ver `bancosDesconectados` em `filaPendencias.ts` pro
 *    porquê das duas;
 * 3. **contas fixas vencidas** — `useAgendadosDoMes` + `montarContasFixas`,
 *    os MESMOS motores da tela Mês. ⛔ Não existe um segundo motor de conta
 *    fixa: "já paguei?" continua sendo "existe transação deste mês apontando
 *    pra este agendado?", decidido num lugar só.
 *
 * ⚠️ `escopo: 'tudo'` (não `'pessoal'`): a fila é sobre TRABALHO a fazer, e
 * um lançamento `#empresa` sem categoria continua sendo trabalho. Filtrar por
 * escopo aqui esconderia pendência real — o oposto do que a tela promete.
 */
export function usePendencias(): Retorno {
  const mes = monthUtils.currentMonth();
  const hoje = monthUtils.currentDay();

  const {
    linhas,
    carregando: carregandoMes,
    erro: erroDoMes,
  } = useMesFinanceiro(mes, 'tudo');
  const {
    agendados,
    carregando: carregandoAgendados,
    erro: erroDeAgendados,
  } = useAgendadosDoMes(mes, linhas, 'tudo');
  const contasFixas = montarContasFixas(agendados, linhas, 'tudo');

  const { data: contas, isLoading: carregandoContas } = useAccounts();
  const falhasDaSessao = useFailedAccounts();

  const [lancamentos, setLancamentos] = useState<LancamentoCru[]>([]);
  const [carregandoLancamentos, setCarregandoLancamentos] = useState(true);
  const [erroDeLancamentos, setErroDeLancamentos] = useState<Error | null>(
    null,
  );
  const [versao, setVersao] = useState(0);

  const recarregar = useCallback(() => setVersao(v => v + 1), []);

  useEffect(() => {
    let cancelado = false;
    setCarregandoLancamentos(true);
    setErroDeLancamentos(null);

    aqlQuery(consultaSemCategoria())
      .then(({ data }) => {
        if (cancelado) return;
        setLancamentos(
          normalizar((data ?? []) as Array<Record<string, unknown>>),
        );
        setCarregandoLancamentos(false);
      })
      .catch((e: unknown) => {
        if (cancelado) return;
        // ⚠️ Falha de leitura NUNCA pode virar "fila vazia" — na tela
        // Pendências, fila vazia é a mensagem de PARABÉNS (spec §5.4).
        // Dizer "está tudo em dia" porque a consulta quebrou é a pior
        // mentira que esta tela poderia contar.
        setLancamentos([]);
        setErroDeLancamentos(e instanceof Error ? e : new Error(String(e)));
        setCarregandoLancamentos(false);
      });

    return () => {
      cancelado = true;
    };
  }, [versao]);

  const erro = erroDeLancamentos ?? erroDoMes ?? erroDeAgendados;
  const carregando =
    carregandoLancamentos ||
    carregandoMes ||
    carregandoAgendados ||
    carregandoContas;

  const contasDoDono: ContaDoDono[] = (contas ?? []).map(c => ({
    id: c.id,
    name: c.name,
    bank_sync_status: c.bank_sync_status ?? null,
    closed: Boolean(c.closed),
  }));

  return {
    // Com erro, devolve a fila VAZIA mas com `erro` preenchido — a tela é
    // quem decide o que mostrar, e ela mostra o erro, nunca o "tudo em dia".
    fila: erro
      ? FILA_VAZIA
      : montarFila({
          lancamentos,
          contas: contasDoDono,
          falhasDaSessao: new Set(falhasDaSessao.keys()),
          contasFixas,
          hoje,
        }),
    carregando,
    erro,
    recarregar,
  };
}

/**
 * ⭐ O CONTADOR DO MENU — o número ao lado de "Pendências" na barra lateral
 * e na barra de abas do celular (spec §3).
 *
 * ```ts
 * import { useContadorPendencias } from '#components/finapp/motor/usePendencias';
 * const { total, carregando } = useContadorPendencias();
 * // total === 0 → não desenhe o badge (fila vazia é o estado saudável)
 * ```
 *
 * @returns `total` — itens da fila INTEIRA (precisa agora + sem categoria);
 *   `carregando` — `true` enquanto alguma das consultas ainda não voltou.
 *   Durante o carregamento `total` é `0`: prefira esconder o badge a piscar
 *   um número que vai mudar.
 *
 * ⚠️⚠️ É o MESMO motor da tela, de propósito. O Actual tem um contador
 * nativo pronto (`bindings.uncategorizedCount()`, uma query agregada) e ele
 * seria mais barato — mas conta OUTRA coisa: só os lançamentos sem
 * categoria, sem os bancos desconectados e sem as fixas vencidas. O menu
 * diria "7" e a tela mostraria 9 itens. Um contador que discorda da tela que
 * ele abre é exatamente o que o spec §5.1 manda evitar.
 *
 * ⚠️ CUSTO, declarado: são três consultas (mês corrente, agendados, sem
 * categoria) e elas rodam em toda tela onde o menu estiver montado. No
 * volume real (~350 linhas/mês + 1.125 sem categoria) isso é barato, mas não
 * é grátis — se um dia pesar, o caminho é subir este hook pra um provider
 * único, não trocar pelo contador nativo (que voltaria a divergir).
 */
export function useContadorPendencias(): {
  total: number;
  carregando: boolean;
} {
  const { fila, carregando } = usePendencias();
  return { total: fila.total, carregando };
}
